/**
 * Manages loading flags, RPA-trigger disabled state, and the CIG-box-note
 * save handler for the Business Office page.
 * @module useBoActionState
 */

import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useMemo, useRef, useState } from "react";
import {
  fetchFcHoldOnPick,
  saveFcHoldComment,
} from "../../../../../api/postCall/fcHold";
import { ToastMessages } from "../../../../../components/toasts/toastMessages";
import { authStatusForDisablingTriggerRpa } from "../../../../../constants/authStatusForDisablingTriggerRpa";
import { useDataAccumulatorForSingleOrder } from "../../../../../context/dataAccumulatorForSingleOrder";
import { BOOptionModel } from "../../../../../data-model/boOptionModel";
import { AllCommentsProps } from "../../../../../data-model/busniessOfficeNote";
import { User } from "../../../../../data-model/firebaseAuthModel";
import { BusniessOfficeReportType } from "../../../../../enums/busniessOfficeReportType";
import { EVWriteBackStatus } from "../../../../../enums/evBvWriteBackStatus";
import { isFcHoldOrg } from "../../../../../enums/organizationTypes";
import { closeModal } from "../../../../../redux/slice/modalSlice";
import { AppDispatch } from "../../../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../../../utils/organizationHelper";
import {
  buildSiblingOrderOverridesResolver,
  saveCigBoxNoteChangesForOrderIds,
} from "../../../../../utils/saveCigBoxNoteChangesForOrderIds";
import { getAllJCodesFromAuthOnFile } from "../../../utils/existingDocsToLs";

/**
 * Dependencies for {@link useBoActionState} supplied by the Business Office page container.
 */
interface UseBoActionStateParams {
  user: User | null | undefined;
  payerName: string;
  providerName: string;
  allCommentsForUI: AllCommentsProps[];
  hasCptTableAnyError: boolean;
  boOptions: BOOptionModel[] | undefined;
  /** Operator-edited FC Hold comment (undefined = untouched this session). */
  fcHoldCommentDraft?: string;
  previousBoStatus?: string;
  dispatch: AppDispatch;
  fetchAll: (orderId: string, orgId: string) => Promise<void>;
}

/**
 * Encapsulates action-related state: loading indicators, the "Trigger RPA"
 * disabled computation, the "hide Trigger RPA" flag, and the CIG-box-note
 * save handler with a 10-second debounce.
 *
 * @param params - Redux/context values required for computing state and
 *   executing actions.
 * @returns Loading flags, disabled/hidden memos, and action handlers.
 */
export const useBoActionState = ({
  // singleMedicalPaOrder,
  user,
  payerName,
  providerName,
  allCommentsForUI,
  hasCptTableAnyError,
  boOptions,
  fcHoldCommentDraft,
  previousBoStatus,
  dispatch,
  fetchAll,
}: UseBoActionStateParams) => {
  const [loading, setLoading] = useState(false);
  const [saveBoLoading, setSaveBoLoading] = useState(false);
  const [rpaTriggered, setRpaTriggered] = useState(false);
  const lastExecutionTimeRef = useRef<number>(0);
  const { authOnFileDetails, generalOrderDetails, filteredSiblingOrders } =
    useDataAccumulatorForSingleOrder();

  // Individual RPA blockers, exposed so the header can show a specific
  // tooltip message per failing condition.
  const rpaBlockers = useMemo(() => {
    const postComments = allCommentsForUI.filter(
      (c) => c.type === BusniessOfficeReportType.FINAL,
    );
    const allHaveStatus = postComments[0]?.comments.every(
      (c) => c.boStatus && c.boStatus !== "",
    );
    const evWriteBackStatus =
      generalOrderDetails?.order?.status?.ev_write_back_status;
    const jCodes = getAllJCodesFromAuthOnFile(
      authOnFileDetails?.auth_on_file ?? {},
    );
    return {
      isRpaInProgress: loading,
      isRpaAlreadyTriggered: rpaTriggered,
      hasCptTableAnyError,
      hasNoBoNotes: allCommentsForUI.length === 0,
      hasNoFinalReportComments: postComments.length === 0,
      hasBoStatusMissing: postComments.length > 0 && !allHaveStatus,
      isEvWriteBackInProgress:
        evWriteBackStatus === EVWriteBackStatus.IN_PROGRESS ||
        evWriteBackStatus === EVWriteBackStatus.QUEUED,
      hasNoJCodes: !jCodes || jCodes.length === 0,
    };
  }, [
    loading,
    rpaTriggered,
    hasCptTableAnyError,
    allCommentsForUI,
    authOnFileDetails?.auth_on_file,
    generalOrderDetails?.order?.status?.ev_write_back_status,
  ]);

  const handleTriggerRpaButtonDisabledState = useMemo(() => {
    return Object.values(rpaBlockers).some(Boolean);
  }, [rpaBlockers]);

  const shouldHideTriggerRpaButton = useMemo(() => {
    return authStatusForDisablingTriggerRpa.includes(
      authOnFileDetails?.auth_on_file?.auth_status ?? "",
    );
  }, [authOnFileDetails?.auth_on_file?.auth_status]);

  /**
   * Triggers CIG box write-back for the parent order and optional linked
   * siblings selected in the Trigger RPA modal.
   *
   * @param selectedOrderIds - Optional linked sibling order ids from the modal
   */
  const handleSaveChanges = async (selectedOrderIds?: string[]) => {
    const currentTime = Date.now();
    const timeSinceLastExecution = currentTime - lastExecutionTimeRef.current;

    if (timeSinceLastExecution < 10000) {
      toast.error(ToastMessages.RETRY_ERROR);
      return;
    }

    lastExecutionTimeRef.current = currentTime;
    setLoading(true);

    const parentOrderId = generalOrderDetails?.order?.order_id ?? "";
    const defaultDrugName =
      generalOrderDetails?.order?.payload?.regimen_name ?? "";
    const defaultDateOfService =
      generalOrderDetails?.order?.payload?.date_of_service ?? "";

    const getOrderOverrides = buildSiblingOrderOverridesResolver(
      parentOrderId,
      filteredSiblingOrders,
      defaultDrugName,
    );

    // FC Hold orgs (OCSRI): record the work start (RISA Working stamp +
    // Auth Initiated value) on the first Trigger RPA; idempotent after that.
    // The write-back triggered next also writes the lifecycle comment to
    // the EMR Comment box.
    const triggerOrgId = getOrgIdForFetchExternalWorklist() || "";
    if (isFcHoldOrg(triggerOrgId)) {
      if (fcHoldCommentDraft !== undefined) {
        try {
          await saveFcHoldComment(
            parentOrderId,
            fcHoldCommentDraft,
            triggerOrgId,
          );
        } catch (err) {
          console.error("FC Hold comment save failed before write-back:", err);
          toast.error(
            "Could not save the FC Hold comment. Trigger RPA was not started.",
          );
          setLoading(false);
          return;
        }
      }
      try {
        await fetchFcHoldOnPick(parentOrderId, triggerOrgId);
      } catch (err) {
        console.error("FC Hold on-pick failed before write-back:", err);
        toast.error(
          "Could not record the FC Hold work start. Trigger RPA was not started.",
        );
        setLoading(false);
        return;
      }
    }

    try {
      const saveResults = (await Promise.race([
        saveCigBoxNoteChangesForOrderIds(
          {
            allCommentsForUI,
            patientMrn:
              generalOrderDetails?.order?.demographics?.patient_id ??
              generalOrderDetails?.order?.demographics?.patient_mrn ??
              "",
            drugName: defaultDrugName,
            dateOfService: defaultDateOfService,
            org_id: getOrgIdForFetchExternalWorklist() || "",
            userEmail: user?.email ?? "",
            userId: user?.id ?? "",
            payerName,
            boOptions: boOptions ?? [],
            providerName,
            previousBoStatus,
          },
          parentOrderId,
          selectedOrderIds,
          getOrderOverrides,
        ).then((results) => {
          const failedResult = results.find(
            (saveResult) => !saveResult.success,
          );
          return failedResult ?? { success: true };
        }),
        new Promise<{ success: boolean; error?: string }>((resolve) =>
          setTimeout(() => resolve({ success: true }), 2000),
        ),
      ])) as { success: boolean; error?: string };
      await fetchAll(parentOrderId, getOrgIdForFetchExternalWorklist() || "");
      setLoading(false);
      dispatch(closeModal());

      if (saveResults.success) {
        setRpaTriggered(true);
        toast.success(ToastMessages.SIG_BOX_NOTE_UPDATED_SUCCESS);
      } else {
        lastExecutionTimeRef.current = 0;
        toast.error(
          saveResults.error || ToastMessages.SIG_BOX_NOTE_UPDATED_FAILURE,
        );
      }
    } catch {
      lastExecutionTimeRef.current = 0;
      setLoading(false);
      dispatch(closeModal());
      toast.error(ToastMessages.SIG_BOX_NOTE_UPDATED_FAILURE);
    }
  };

  return {
    loading,
    saveBoLoading,
    setSaveBoLoading,
    handleTriggerRpaButtonDisabledState,
    rpaBlockers,
    shouldHideTriggerRpaButton,
    handleSaveChanges,
  };
};
